import { createContext, useState, useContext, useEffect, useCallback } from "react";
import api from "../api/axios";
import { DEFAULT_DEPARTMENT, normalizeDepartment } from "../constants/departments";

export const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const LEGACY_AUTH_KEYS = ["role", "user", "userStatus", "needsOnboarding"];

function mapServerSession(data) {
  const needsOnboarding = Boolean(
    data.needsOnboarding ?? data.onboarding_complete === false
  );
  return {
    id: data.id ?? data.userId,
    email: data.email,
    role: data.role,
    full_name: data.full_name,
    team: data.team,
    department: normalizeDepartment(data.department || DEFAULT_DEPARTMENT),
    // Only default when the server genuinely omitted the field. Coercing a
    // real "pending" to "approved" would drop a user onto a dashboard where
    // every request 403s, with no way back to the pending screen.
    status: data.status ?? "approved",
    needsOnboarding,
    permissions: Array.isArray(data.permissions) ? data.permissions : [],
    can_edit_matrix: !!data.can_edit_matrix,
    can_delegate_grants: !!data.can_delegate_grants,
  };
}

export const AuthProvider = ({ children }) => {
  const [role, setRole] = useState(null);
  const [status, setStatus] = useState(null);
  const [user, setUser] = useState(null);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [sessionReady, setSessionReady] = useState(() => !localStorage.getItem("token"));

  const syncSessionFromServer = useCallback((data) => {
    const merged = mapServerSession(data);
    const normalizedRole = String(merged.role || "").toLowerCase();
    setRole(normalizedRole || null);
    setStatus(merged.status);
    setNeedsOnboarding(merged.needsOnboarding);
    setUser(merged);
    return merged;
  }, []);

  useEffect(() => {
    LEGACY_AUTH_KEYS.forEach((k) => localStorage.removeItem(k));

    const token = localStorage.getItem("token");
    if (!token) {
      setSessionReady(true);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) syncSessionFromServer(data);
      } catch (err) {
        if (!cancelled && err.response?.status === 401) {
          localStorage.removeItem("token");
          setRole(null);
          setStatus(null);
          setUser(null);
          setNeedsOnboarding(false);
        }
      } finally {
        if (!cancelled) setSessionReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [syncSessionFromServer]);

  useEffect(() => {
    const onResync = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      try {
        const { data } = await api.get("/auth/me");
        syncSessionFromServer(data);
      } catch {
        /* ignore */
      }
    };
    const onOnboardingRequired = () => setNeedsOnboarding(true);
    window.addEventListener("auth:resync", onResync);
    window.addEventListener("auth:onboarding-required", onOnboardingRequired);
    return () => {
      window.removeEventListener("auth:resync", onResync);
      window.removeEventListener("auth:onboarding-required", onOnboardingRequired);
    };
  }, [syncSessionFromServer]);

  const login = (token, newRole, userData = {}) => {
    localStorage.setItem("token", token);
    syncSessionFromServer({
      id: userData.id ?? userData.userId,
      email: userData.email,
      role: newRole,
      status: userData.status || "approved",
      permissions: userData.permissions,
      needsOnboarding: userData.needsOnboarding,
      can_edit_matrix: userData.can_edit_matrix,
      can_delegate_grants: userData.can_delegate_grants,
      full_name: userData.full_name,
      team: userData.team,
      department: userData.department,
    });
    setSessionReady(true);
  };

  const logout = () => {
    localStorage.removeItem("token");
    LEGACY_AUTH_KEYS.forEach((k) => localStorage.removeItem(k));
    setRole(null);
    setStatus(null);
    setUser(null);
    setNeedsOnboarding(false);
    setSessionReady(true);
  };

  const refreshSession = (token, newRole, userData = {}) => {
    login(token, newRole, { ...userData, needsOnboarding: false });
  };

  const updateUser = (userData) => {
    setUser((prev) => ({ ...prev, ...userData }));
  };

  const isApproved = () => status === "approved" || user?.status === "approved";

  return (
    <AuthContext.Provider
      value={{
        role,
        status,
        user,
        needsOnboarding,
        sessionReady,
        login,
        logout,
        refreshSession,
        updateUser,
        syncSessionFromServer,
        isApproved,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
